import { ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { TranslateService } from "@ngx-translate/core";
import { FileRestrictions, SuccessEvent, UploadEvent } from "@progress/kendo-angular-upload";
import { DestroyBase } from '../ng-destroy-base';
import { AppServiceHelperService } from '../../../services/app-service-helper.service';
import { EventBusService } from '../../../services/event-bus.service';

/**
 * Import dialog
 */
@Component({
  selector: 'app-import-dialog',
  templateUrl: './app-import-dialog.component.html',
  styleUrls: ['./app-import-dialog.component.css'],
  encapsulation: ViewEncapsulation.None,
})
export class AppImportDialogComponent extends DestroyBase implements OnInit {

  /**
   * Import area
   */
  importArea: any;

  /**
   * Is dialog opened
   */
  isDialogOpened = false;

  /**
   * Template name
   */
  templateName: any;

  /**
   * Additional data
   */
  additionalData: any;

  /**
   * Callback
   */
  callback: any;

  /**
   * Dialog title
   */
  public dialogTitle = "";

  /**
   * List items
   */
  listItems: any;

  /**
   * Valuation type
   */
  valuationType = 0;

  /**
   * Valuation types options
   */
  valuationTypesOptions: any[];

  /**
   * Save url
   */
  saveUrl: any;

  /**
   * Select label
   */
  selectLabel: any;

  /**
   * Cancel label
   */
  cancelLabel: any;

  /**
   * Clear selected files label
   */
  clearSelectedFilesLabel: any;

  /**
   * Drop files here label
   */
  dropFilesHereLabel: any;

  /**
   * Header status uploaded label
   */
  headerStatusUploadedLabel: any;

  /**
   * Header status uploading label
   */
  headerStatusUploadingLabel: any;

  /**
   * Invalid file extension label
   */
  invalidFileExtensionLabel: any;

  /**
   * Invalid files label
   */
  invalidFilesLabel: any;

  /**
   * Invalid max file size label
   */
  invalidMaxFileSizeLabel: any;

  /**
   * Invalid min file size label
   */
  invalidMinFileSizeLabel: any;

  /**
   * Remove label
   */
  removeLabel: any;

  /**
   * Retry label
   */
  retryLabel: any;

  /**
   * Status failed label
   */
  statusFailedLabel: any;

  /**
   * Status uploaded label
   */
  statusUploadedLabel: any;

  /**
   * Upload selected files label
   */
  uploadSelectedFilesLabel: any;

  /**
   * Restrictions
   */
  restrictions: FileRestrictions = {
    allowedExtensions: [".xlsx"],
  };

  /**
   * Constructor
   * @param eventBus EventBusService
   * @param ref ChangeDetectorRef
   * @param _translate TranslateService
   * @param appServiceHelper AppServiceHelperService
   */
  constructor(private eventBus: EventBusService,
    private ref: ChangeDetectorRef,
    private _translate: TranslateService,
    private appServiceHelper: AppServiceHelperService) {
    super();
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {

    this.valuationTypesOptions = [
      { "Oid": 0, "Name": this._translate.instant('import.valuation') },
      { "Oid": 1, "Name": this._translate.instant('import.valuationvpv') }
    ];

    this.saveUrl = this.appServiceHelper.getServiceEndpoint('pollux.import.api', 'import');

    this.selectLabel = this._translate.instant('import.select');
    this.cancelLabel = this._translate.instant('import.cancel');
    this.clearSelectedFilesLabel = this._translate.instant('import.clearSelectedFiles');
    this.dropFilesHereLabel = this._translate.instant('import.dropFilesHere');
    this.headerStatusUploadedLabel = this._translate.instant('import.headerStatusUploaded');
    this.headerStatusUploadingLabel = this._translate.instant('import.headerStatusUploading');
    this.invalidFileExtensionLabel = this._translate.instant('import.invalidFileExtension');
    this.invalidMaxFileSizeLabel = this._translate.instant('import.invalidMaxFileSize');
    this.invalidMinFileSizeLabel = this._translate.instant('import.invalidMinFileSize');
    this.removeLabel = this._translate.instant('import.remove');
    this.retryLabel = this._translate.instant('import.retry');
    this.uploadSelectedFilesLabel = this._translate.instant('import.uploadSelectedFiles');
    this.invalidFilesLabel = this._translate.instant('import.invalidFiles');
    this.statusFailedLabel = this._translate.instant('import.statusFailed');
    this.statusUploadedLabel = this._translate.instant('import.statusUploaded');

    this.onEvents.push(this.eventBus.subscribeEvent('startAppImport', (item, _options) => {
      this.importArea = item.importArea;
      this.templateName = item.templateName || null;
      this.additionalData = item.additionalData || null;
      this.callback = item.callback;

      this.saveUrl = this.appServiceHelper.getServiceEndpoint('pollux.import.api', 'import/') + (this.templateName || this.importArea);
      this.dialogTitle = this._translate.instant('import.valuationPortfolioImport' + (this.importArea || ''));
      this.ref.markForCheck();
      setTimeout(() => {
        this.isDialogOpened = true;
        this.ref.markForCheck();
      }, 10);
    }));
  }

  /**
   * Cancel action
   */
  cancel() {
    this.isDialogOpened = false;
    this.importArea = null;
    this.templateName = null;
    this.additionalData = null;
    this.valuationType = 0;
  }

  /**
   * Upload event handler
   * @param e Upload Event
   */
  uploadEventHandler(e: UploadEvent) {
    //event is triggered before upload to server is sent

    //set additional data if any
    e.data = { additionalData: this.additionalData ? JSON.stringify(this.additionalData) : "{}" };
  }

  /**
   * Success event handler
   * @param e Success Event
   */
  successEventHandler(e: SuccessEvent) {
    this.eventBus.broadcastEvent("openConfirmDialog", {
      success: ()=> {
        this.isDialogOpened = false;
        this.valuationType = 0;
        setTimeout(() => {
          switch (this.importArea) {
            case 'ReportRentRoll': {
              this.eventBus.broadcastEvent('TenantLoadData');
              this.importArea = null;
              break;
            }
            case 'ReportValuation': {
              this.eventBus.broadcastEvent('ValuationLoadData');
              this.importArea = null;
              break;
            }
            case 'ReportCompetitorOverview': {
              this.eventBus.broadcastEvent('CompetitorLoadData');
              this.importArea = null;
              break;
            }
            case 'Objects': {
              this.eventBus.broadcastEvent('comparables.property.saved');
              this.importArea = null;
              break;
            }
            case 'Lease': {
              this.eventBus.broadcastEvent('comparables.leases.saved');
              this.importArea = null;
              break;
            }
            case 'Valuation': {
              this.eventBus.broadcastEvent('comparables.valuation.saved');
              this.importArea = null;
              break;
            }
            case 'Sales': {
              let broadcastMessage = 'nothing';
              switch (this.templateName) {
                case 'SalesTransaction':
                  broadcastMessage = 'comparables.sale.saved';
                  break;
                case 'SalesTransactionHealthcare':
                  broadcastMessage = 'comparables.sale.healthcare.saved';
                  break;
                case 'SalesTransactionIndustrial':
                  broadcastMessage = 'comparables.sale.industrial.saved';
                  break;
                case 'SalesTransactionLogistic':
                  broadcastMessage = 'comparables.sale.logistic.saved';
                  break;
                case 'SalesTransactionParking':
                  broadcastMessage = 'comparables.sale.parking.saved';
                  break;
                case 'SalesTransactionResidential':
                  broadcastMessage = 'comparables.sale.residential.saved';
                  break;
                case 'SalesTransactionRetail':
                  broadcastMessage = 'comparables.sale.retail.saved';
                  break;
              }
              this.eventBus.broadcastEvent(broadcastMessage);
              this.importArea = null;
              break;
            }
          }
          if (this.callback) {
            this.callback();
            this.callback = null;
          }
        }, 10);
      }
    }, 'importStatusUploaded');
  }
}
